import { FeatureKey } from '../../types/enums/feature.enum'
import { PlanTier } from '../../types/enums/plan.enum'

/*
 * A notification's types, what each stores in `data`, and what the API answers (docs/notifications.md). All in one
 * file on purpose: they change together.
 */

export enum NotificationTypes {
  ClientJoinEvent = 'client-join-event',
  ClientEventCancelled = 'client-event-cancelled',

  /** A visitor was turned away on the public page because the plan is used up. */
  PlanLimitPublicRefusal = 'plan-limit-public-refusal',

  ClientReservationCreated = 'client-reservation-created',
  ClientReservationCancelled = 'client-reservation-cancelled',

  /** A person joined the premise from the client zone. Before 2026-10-29 these rows had no type. */
  CustomerJoinedEntity = 'customer-joined-entity',
}

/** A client booked a reservation, or it was cancelled through the link in an e-mail. */
export interface ReservationNotification {
  reservationId: string
  start: Date
  end: Date
  activityId?: string | undefined
  activityName?: string | undefined
  customerId?: string | undefined
  /** The member of staff who cancelled through the premise's link; never the customer's account. */
  userId?: string | undefined
  bookerFirstName: string
  bookerLastName: string
  bookerEmail: string
  bookerPhone: string
  createdAt: Date
}

/** A customer signed up for an event, or cancelled their sign-up through its link. */
export interface JoinEventNotification {
  eventId: string
  /** Opens the participant's panel on the event; absent on rows from before 2026-10-29. */
  participationId?: string
  /** Confirmed participants right after the sign-up or cancel, and the capacity (0 = uncapped) then. */
  participants?: number
  capacity?: number
  name: string
  start: Date | null
  end: Date | null
  activityId: string
  activityName: string
  customerId: string
  /** What the sign-up typed (its snapshot), never the customer's account. */
  customerFirstName: string
  customerLastName: string
  customerEmail: string
  customerPhone: string
  createdAt: Date
}

/** The premise lost a client: a public booking or sign-up was refused by its plan limit. */
export interface PlanLimitRefusalNotification {
  feature: FeatureKey
  context: 'booking' | 'eventSignUp'
  /** The service or event the visitor wanted. */
  what: string
  /** Its termín, in the premise's zone. */
  when: string
  tier: PlanTier
  limit: number | null
  used: number
  /** Refusals since the previous notice, which were not notified one by one. */
  missedSinceLast: number
}

/** A person joined the premise from the client zone: a new client row, linked to their account. */
export interface CustomerJoinedNotification {
  customerId: string
  firstName: string
  lastName: string
}

/** What each type stores in `data`. The sentence is composed on the frontend from it; the backend stores none. */
export type NotificationDataMap = {
  [NotificationTypes.ClientReservationCreated]: ReservationNotification
  [NotificationTypes.ClientReservationCancelled]: ReservationNotification
  [NotificationTypes.ClientJoinEvent]: JoinEventNotification
  [NotificationTypes.ClientEventCancelled]: JoinEventNotification
  [NotificationTypes.PlanLimitPublicRefusal]: PlanLimitRefusalNotification
  [NotificationTypes.CustomerJoinedEntity]: CustomerJoinedNotification
}

export const NOTIFICATION_TYPES = Object.values(NotificationTypes)

export function isNotificationType(value: unknown): value is NotificationTypes {
  return typeof value === 'string' && (NOTIFICATION_TYPES as string[]).includes(value)
}

interface NotificationBase {
  id: string
  read: boolean
  createdAt: Date
  /** The premise it is about; null only for an owner of several premises' rows from before 2026-10-29. */
  entityId: string | null
  /** The premise's IANA zone, for the day groups and the times; null with `entityId`. */
  timezone: string | null
}

export type TypedNotification = {
  [K in NotificationTypes]: NotificationBase & { type: K; data: NotificationDataMap[K] }
}[NotificationTypes]

/** A row from before the types: its stored text, shown as it is, leading nowhere. */
export interface UntypedNotification extends NotificationBase {
  type: null
  title: string
  description: string
}

export type Notification = TypedNotification | UntypedNotification

/** The page's fixed views (its tabs). `types` null means every type. */
export const NOTIFICATION_VIEWS = {
  all: { types: null, unread: false },
  unread: { types: null, unread: true },
  cancellations: {
    types: [NotificationTypes.ClientReservationCancelled, NotificationTypes.ClientEventCancelled],
    unread: false,
  },
  refusals: { types: [NotificationTypes.PlanLimitPublicRefusal], unread: false },
} as const satisfies Record<string, { types: readonly NotificationTypes[] | null; unread: boolean }>

export type NotificationView = keyof typeof NOTIFICATION_VIEWS
export const notificationViews = Object.keys(NOTIFICATION_VIEWS) as NotificationView[]

/** The panel's chips. Klienti holds both the new client and the one the plan turned away. */
export const NOTIFICATION_CHIPS = {
  all: null,
  reservations: [NotificationTypes.ClientReservationCreated, NotificationTypes.ClientReservationCancelled],
  events: [NotificationTypes.ClientJoinEvent, NotificationTypes.ClientEventCancelled],
  clients: [NotificationTypes.CustomerJoinedEntity, NotificationTypes.PlanLimitPublicRefusal],
} as const satisfies Record<string, readonly NotificationTypes[] | null>

export type NotificationChip = keyof typeof NOTIFICATION_CHIPS
